/**
 * Environment capability checks, run before anything expensive is loaded.
 *
 * The most likely real-world entry failure for a QR-launched demo in Korea is not a bug in our
 * code: it is the QR being scanned inside KakaoTalk or Naver, whose in-app webviews commonly
 * block `getUserMedia` outright. Without this check the user sees a permission prompt that never
 * appears and a scan screen that never advances.
 */

export type UnsupportedReason = 'inAppBrowser' | 'noWebgl' | 'insecure' | 'noCamera';

export interface EnvironmentReport {
  readonly ok: boolean;
  readonly reason: UnsupportedReason | null;
  readonly isIos: boolean;
  readonly isAndroid: boolean;
}

/** In-app webviews that are known to block or silently fail camera capture. */
const IN_APP_PATTERNS = [
  /KAKAOTALK/i,
  /NAVER\(inapp/i,
  /\bDaumApps\b/i,
  /Instagram/i,
  /\bFBAN\b|\bFBAV\b/i,
  /\bLine\//i,
  /everytimeApp/i,
];

export function detectEnvironment(ua: string = navigator.userAgent): EnvironmentReport {
  const isIos = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const base = { isIos, isAndroid };

  if (IN_APP_PATTERNS.some((re) => re.test(ua))) {
    return { ok: false, reason: 'inAppBrowser', ...base };
  }
  // getUserMedia needs a secure context. localhost is exempt; a LAN IP over http is not.
  if (!window.isSecureContext) {
    return { ok: false, reason: 'insecure', ...base };
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return { ok: false, reason: 'noCamera', ...base };
  }
  if (!hasWebgl()) {
    return { ok: false, reason: 'noWebgl', ...base };
  }
  return { ok: true, reason: null, ...base };
}

function hasWebgl(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') ?? canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Camera permission
// ---------------------------------------------------------------------------

export type PermissionOutcome =
  | { state: 'granted' }
  /** The user dismissed a prompt that was actually shown — retrying can still work. */
  | { state: 'denied'; sticky: false }
  /** The origin is blocked. Retrying can NEVER re-prompt; only browser settings or a reload help. */
  | { state: 'denied'; sticky: true }
  | { state: 'unavailable'; reason: UnsupportedReason };

/**
 * Probe camera access from inside a user gesture.
 *
 * Distinguishing sticky from transient denial matters more than it looks: on both iOS Safari and
 * Android Chrome a denial is remembered per origin, so an in-page "다시 시도" button gets an
 * instant NotAllowedError with no prompt, forever. Shipping that button without the distinction
 * means one accidental "Don't Allow" ends the demo on that device.
 */
export async function probeCameraPermission(): Promise<PermissionOutcome> {
  // Chromium exposes the stored decision directly. Safari does not implement the 'camera' name.
  const queried = await queryPermissionState();
  if (queried === 'granted') return { state: 'granted' };
  if (queried === 'denied') return { state: 'denied', sticky: true };

  const startedAt = performance.now();
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
    // Release immediately — MindAR opens its own stream, and two captures fight on iOS.
    for (const track of stream.getTracks()) track.stop();
    return { state: 'granted' };
  } catch (err) {
    const name = (err as DOMException | undefined)?.name ?? '';
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      return { state: 'unavailable', reason: 'noCamera' };
    }
    if (name === 'NotReadableError') {
      return { state: 'unavailable', reason: 'noCamera' };
    }
    // Heuristic for engines without permissions.query: a real prompt takes a human moment to
    // dismiss. An instant rejection means the decision was already stored.
    const INSTANT_MS = 250;
    const sticky = performance.now() - startedAt < INSTANT_MS;
    return { state: 'denied', sticky };
  }
}

async function queryPermissionState(): Promise<PermissionState | null> {
  if (!navigator.permissions?.query) return null;
  try {
    const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
    return status.state;
  } catch {
    return null; // Safari: unsupported permission name
  }
}
